import { rbimQuestionnairePayloadV1Schema } from "@rbim/shared";
import type { LocationContext, QuestionnairePayload } from "./questionnaire-model";
import { applyAuthoritativeLocation } from "./questionnaire-model";

const DRAFT_VERSION = 1 as const;
const DRAFT_PREFIX = "rbim-web-questionnaire-draft";
const LOCATION_PREFIX = "rbim-web-questionnaire-location";

type BrowserStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

type QuestionnaireDraftEnvelope = {
  version: typeof DRAFT_VERSION;
  ownerId: string;
  municipalityId: string;
  barangayId: string;
  savedAt: string;
  payload: QuestionnairePayload;
};

export type RestoredQuestionnaireDraft = {
  payload: QuestionnairePayload;
  savedAt: string;
};

export function questionnaireDraftKey(ownerId: string, context: LocationContext) {
  return [DRAFT_PREFIX, DRAFT_VERSION, ownerId, context.municipality.id, context.barangay.id]
    .map(encodeURIComponent)
    .join(":");
}

export function selectedBarangayStorageKey(ownerId: string, municipalityId: string) {
  return [LOCATION_PREFIX, DRAFT_VERSION, ownerId, municipalityId]
    .map(encodeURIComponent)
    .join(":");
}

export function loadQuestionnaireDraft(
  ownerId: string,
  context: LocationContext,
  storage = getBrowserStorage(),
): RestoredQuestionnaireDraft | null {
  if (!storage) return null;
  const key = questionnaireDraftKey(ownerId, context);

  try {
    const serialized = storage.getItem(key);
    if (!serialized) return null;
    const candidate = JSON.parse(serialized) as Partial<QuestionnaireDraftEnvelope>;
    if (
      candidate.version !== DRAFT_VERSION
      || candidate.ownerId !== ownerId
      || candidate.municipalityId !== context.municipality.id
      || candidate.barangayId !== context.barangay.id
      || typeof candidate.savedAt !== "string"
    ) {
      return null;
    }
    if (!isDraftPayloadShape(candidate.payload)) return null;
    return {
      payload: applyAuthoritativeLocation(candidate.payload, context),
      savedAt: candidate.savedAt,
    };
  } catch {
    return null;
  }
}

export function saveQuestionnaireDraft(
  ownerId: string,
  context: LocationContext,
  payload: QuestionnairePayload,
  storage = getBrowserStorage(),
) {
  if (!storage) return null;
  const savedAt = new Date().toISOString();
  const envelope: QuestionnaireDraftEnvelope = {
    version: DRAFT_VERSION,
    ownerId,
    municipalityId: context.municipality.id,
    barangayId: context.barangay.id,
    savedAt,
    payload: applyAuthoritativeLocation(payload, context),
  };

  try {
    storage.setItem(questionnaireDraftKey(ownerId, context), JSON.stringify(envelope));
    return savedAt;
  } catch {
    return null;
  }
}

export function clearQuestionnaireDraft(
  ownerId: string,
  context: LocationContext,
  storage = getBrowserStorage(),
) {
  if (!storage) return false;
  try {
    storage.removeItem(questionnaireDraftKey(ownerId, context));
    return true;
  } catch {
    return false;
  }
}

function getBrowserStorage(): BrowserStorage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

function isDraftPayloadShape(value: unknown): value is QuestionnairePayload {
  if (!isRecord(value) || !isRecord(value.household) || !isRecord(value.householdQuestions)
    || !isRecord(value.encoding) || !Array.isArray(value.interviews) || value.interviews.length !== 2
    || !value.interviews.every(isRecord) || !Array.isArray(value.members) || value.members.length === 0
    || !value.members.every(isRecord)) return false;

  try {
    const payload = value as QuestionnairePayload;
    const shapeProbe: QuestionnairePayload = {
      ...payload,
      household: {
        ...payload.household,
        province: payload.household.province || "Draft province",
        municipality: payload.household.municipality || "Draft municipality",
        barangay: payload.household.barangay || "Draft barangay",
        address: payload.household.address || "Draft address",
        householdCode: payload.household.householdCode || "DRAFT",
        respondentName: payload.household.respondentName || "Draft respondent",
        householdHead: payload.household.householdHead || "Draft head",
        totalMembers: String(payload.members.length),
      },
      members: payload.members.map((member) => ({
        ...member,
        firstName: member.firstName || "Draft",
        lastName: member.lastName || "Member",
        sex: member.sex || "1",
        relationshipToHead: member.relationshipToHead || "01",
        placeOfBirth: member.placeOfBirth || "Draft place",
        nationality: member.nationality || "1",
        dateOfBirth: "",
        dateOfBirthUnknown: true,
        reportedAge: 0,
        ageSource: "reported",
      })),
    };
    return rbimQuestionnairePayloadV1Schema.safeParse(shapeProbe).success;
  } catch {
    return false;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
